import { useEffect, useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const VERDICT_LABELS = {
  genuinely_zero_in_source: 'Genuinely zero in source',
  source_has_amount: 'Source has amount',
  not_found_in_source: 'Not found in source',
  fetch_failed: 'Fetch failed',
  no_source_id: 'No source id',
  not_yet_run: 'Not yet run',
};

function fmtTimestamp(isoString) {
  if (!isoString) return 'never';
  const then = new Date(isoString);
  if (Number.isNaN(then.getTime())) return 'never';
  return then.toLocaleString('en-GB');
}

// Read-only sizing of the "amount==0 and amount_parse_suspect==false"
// population (Invoice Row Explain's own invariant says this combination is
// impossible -- it isn't, for a historical pre-fix cohort; see
// services/invoice_zero_amount_census.py) plus a heuristic currency-default
// census and a receipt/invoice sibling-pair check (Anthropic sends a
// receipt AND an invoice per charge; the receipt's amount extraction can
// fail while the invoice's succeeds).
export function ZeroAmountInvoiceCensusPanel() {
  const [zeroCensus, setZeroCensus] = useState(null);
  const [zeroLoading, setZeroLoading] = useState(false);
  const [currencyCensus, setCurrencyCensus] = useState(null);
  const [currencyLoading, setCurrencyLoading] = useState(false);
  const [sourceVerification, setSourceVerification] = useState(null);
  const [sourceVerificationLoading, setSourceVerificationLoading] = useState(false);
  const [parsedPairCensus, setParsedPairCensus] = useState(null);
  const [parsedPairLoading, setParsedPairLoading] = useState(false);
  const [closeoutSummary, setCloseoutSummary] = useState(null);
  const [dupMergePreview, setDupMergePreview] = useState(null);
  const [dupMergeLoading, setDupMergeLoading] = useState(false);
  const [dupMergeApplyLoading, setDupMergeApplyLoading] = useState(false);
  const [terminalPreview, setTerminalPreview] = useState(null);
  const [terminalLoading, setTerminalLoading] = useState(false);
  const [terminalApplyLoading, setTerminalApplyLoading] = useState(false);
  const [disposition, setDisposition] = useState(null);
  const [dispositionLoading, setDispositionLoading] = useState(false);

  // Persisted verdicts load on mount so a reload shows the last Run's
  // result without requiring the operator to click Run again.
  useEffect(() => {
    let cancelled = false;
    diagnosticsAPI.invoiceZeroAmountSourceVerification()
      .then(({ data }) => {
        if (!cancelled) setSourceVerification(data);
      })
      .catch(() => {
        // Best-effort preload; the Run button still works if this fails.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadCloseoutSummary = () => {
    diagnosticsAPI.invoiceZeroAmountCloseoutSummary()
      .then(({ data }) => setCloseoutSummary(data))
      .catch(() => {
        // Best-effort; the counters just stay stale until the next load.
      });
  };

  useEffect(() => {
    loadCloseoutSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runDuplicateMergePreview = async () => {
    setDupMergeLoading(true);
    try {
      const { data } = await diagnosticsAPI.invoiceZeroAmountDuplicateMerge();
      setDupMergePreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDupMergeLoading(false);
    }
  };

  const applyDuplicateMerge = async () => {
    setDupMergeApplyLoading(true);
    try {
      const { data } = await diagnosticsAPI.invoiceZeroAmountDuplicateMergeApply();
      toast.success(`Merged ${data.applied_count} zero-amount duplicate${data.applied_count === 1 ? '' : 's'}`);
      await runDuplicateMergePreview();
      loadCloseoutSummary();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDupMergeApplyLoading(false);
    }
  };

  const runTerminalPreview = async () => {
    setTerminalLoading(true);
    try {
      const { data } = await diagnosticsAPI.invoiceZeroAmountTerminal();
      setTerminalPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setTerminalLoading(false);
    }
  };

  const applyTerminal = async () => {
    setTerminalApplyLoading(true);
    try {
      const { data } = await diagnosticsAPI.invoiceZeroAmountTerminalApply();
      toast.success(`Classified ${data.applied_count} invoice${data.applied_count === 1 ? '' : 's'} as nothing due`);
      await runTerminalPreview();
      loadCloseoutSummary();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setTerminalApplyLoading(false);
    }
  };

  const runDisposition = async () => {
    setDispositionLoading(true);
    try {
      const { data } = await diagnosticsAPI.invoiceZeroAmountDisposition();
      setDisposition(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDispositionLoading(false);
    }
  };

  const runSourceVerification = async () => {
    setSourceVerificationLoading(true);
    try {
      const { data } = await diagnosticsAPI.invoiceZeroAmountSourceVerificationRun();
      setSourceVerification(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSourceVerificationLoading(false);
    }
  };

  const runZeroCensus = async () => {
    setZeroLoading(true);
    try {
      const { data } = await diagnosticsAPI.invoiceZeroAmountCensus();
      setZeroCensus(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setZeroLoading(false);
    }
  };

  const runCurrencyCensus = async () => {
    setCurrencyLoading(true);
    try {
      const { data } = await diagnosticsAPI.invoiceCurrencyDefaultCensus();
      setCurrencyCensus(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCurrencyLoading(false);
    }
  };

  const runParsedPairCensus = async () => {
    setParsedPairLoading(true);
    try {
      const { data } = await diagnosticsAPI.invoiceParsedPairDuplicateCensus();
      setParsedPairCensus(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setParsedPairLoading(false);
    }
  };

  const dup = zeroCensus?.duplicate_notification_check;
  const reextraction = zeroCensus?.reextraction_backfill_status;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Zero-Amount Invoice Census</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Read-only. Sizes the "amount==0, amount_parse_suspect==false" population --
          a historical pre-fix cohort (commit 81175109) that
          services/gmail_amount_reextraction.py's backfill repairs, not a live gate
          bypass. Also checks whether a zero-amount row is a duplicate notification
          (a vendor's receipt email whose amount extraction failed, paired with an
          invoice email for the same charge that parsed correctly) and a heuristic
          currency-default proxy.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="zero-amount-census-run" onClick={runZeroCensus} disabled={zeroLoading} size="sm">
            {zeroLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Run zero-amount census
          </Button>
          <Button data-testid="currency-default-census-run" onClick={runCurrencyCensus} disabled={currencyLoading} size="sm" variant="outline">
            {currencyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Run currency-default census
          </Button>
          <Button
            data-testid="source-verification-run"
            onClick={runSourceVerification}
            disabled={sourceVerificationLoading}
            size="sm"
            variant="outline"
          >
            {sourceVerificationLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Run approved-zero source verification
          </Button>
          <Button data-testid="parsed-pair-census-run" onClick={runParsedPairCensus} disabled={parsedPairLoading} size="sm" variant="outline">
            {parsedPairLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Run parsed-pair duplicate census
          </Button>
        </div>

        {closeoutSummary && (
          <div className="border rounded-lg p-3 text-xs" data-testid="zero-amount-closeout-summary">
            <div className="font-medium mb-1">Closeout status</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1">
              <div><span className="text-muted-foreground">Merged:</span> <span className="font-medium">{closeoutSummary.merged_count}</span></div>
              <div><span className="text-muted-foreground">Nothing due:</span> <span className="font-medium">{closeoutSummary.nothing_due_count}</span></div>
              <div>
                <span className="text-muted-foreground">Remaining gmail parse cohort:</span>{' '}
                <span className="font-medium">{closeoutSummary.remaining_gmail_parse_cohort ?? 'n/a'}</span>
              </div>
              <div><span className="text-muted-foreground">Needs human look:</span> <span className="font-medium">{closeoutSummary.needs_human_look_count}</span></div>
            </div>
          </div>
        )}

        {zeroCensus && (
          <div className="border rounded-lg p-3 space-y-3 text-xs" data-testid="zero-amount-census-result">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1">
              <div><span className="text-muted-foreground">Total count:</span> <span className="font-medium">{zeroCensus.total_count}</span></div>
              <div><span className="text-muted-foreground">has_pdf=true:</span> {zeroCensus.has_pdf_true_count}</div>
              <div><span className="text-muted-foreground">Already reextraction-touched but still 0:</span> {zeroCensus.already_reextraction_touched_but_still_zero}</div>
              <div><span className="text-muted-foreground">Remaining backfill cohort:</span> {reextraction?.available ? reextraction.remaining_cohort_count : 'n/a'}</div>
            </div>

            <div>
              <div className="font-medium mb-1">By source / review_status</div>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-1 pr-2">source | review_status</th>
                    <th className="py-1 pr-2 text-right">count</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(zeroCensus.by_source_and_review_status || {}).map(([k, v]) => (
                    <tr key={k} className="border-b border-border/30">
                      <td className="py-1 pr-2 font-mono">{k}</td>
                      <td className="py-1 pr-2 text-right font-mono">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {reextraction?.latest_apply_chain && (
              <div className="text-muted-foreground" data-testid="zero-amount-census-chain-status">
                Reextraction backfill Apply chain: {reextraction.latest_apply_chain.status}, run{' '}
                {reextraction.latest_apply_chain.runs_count}, last active{' '}
                {fmtTimestamp(reextraction.latest_apply_chain.heartbeat_at)}
              </div>
            )}
            {reextraction?.available && !reextraction.latest_apply_chain && (
              <div className="text-muted-foreground" data-testid="zero-amount-census-chain-status">
                Reextraction backfill Apply chain has never run in this environment.
              </div>
            )}

            {dup && (
              <div className="border-t pt-2" data-testid="zero-amount-census-duplicate-check">
                <div className="font-medium mb-1">Duplicate-notification check</div>
                <div>
                  Docs with an invoice_number: {dup.docs_with_invoice_number}, checked: {dup.checked}
                  {!dup.checked_is_full_population && ' (bounded, not the full population)'}
                </div>
                <div>
                  Sibling with a real amount found: <span className="font-medium">{dup.sibling_found_count}</span>
                  {dup.pair_rate !== null && ` (pair rate ${(dup.pair_rate * 100).toFixed(1)}%)`}
                </div>
                <div className="text-muted-foreground">
                  Already flagged at ingest (forward guard, not this census's retroactive check):{' '}
                  <span className="font-medium">{dup.already_flagged_at_ingest_count}</span>
                </div>
                {dup.samples?.length > 0 && (
                  <table className="w-full text-xs border-collapse mt-1">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1 pr-2">Zero doc</th>
                        <th className="py-1 pr-2">Invoice number</th>
                        <th className="py-1 pr-2">Sibling doc</th>
                        <th className="py-1 pr-2 text-right">Sibling amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dup.samples.map((s) => (
                        <tr key={s.zero_doc_id} className="border-b border-border/30">
                          <td className="py-1 pr-2 font-mono">{s.zero_doc_id}</td>
                          <td className="py-1 pr-2 font-mono">{s.invoice_number}</td>
                          <td className="py-1 pr-2 font-mono">{s.sibling_id}</td>
                          <td className="py-1 pr-2 text-right font-mono">{s.sibling_amount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        {currencyCensus && (
          <div className="border rounded-lg p-3 space-y-2 text-xs" data-testid="currency-default-census-result">
            <div className="font-medium">Currency-default census (gmail, heuristic)</div>
            <p className="text-muted-foreground">{currencyCensus.note}</p>
            <div className="grid grid-cols-3 gap-x-4">
              {Object.entries(currencyCensus.by_bucket || {}).map(([k, v]) => (
                <div key={k}><span className="text-muted-foreground">{k}:</span> <span className="font-medium">{v}</span></div>
              ))}
            </div>
            {currencyCensus.signal_contradicts_stored_samples?.length > 0 && (
              <div>
                <div className="font-medium mt-1">Signal contradicts stored (strongest evidence of a wrong currency)</div>
                <table className="w-full text-xs border-collapse mt-1">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1 pr-2">id</th>
                      <th className="py-1 pr-2">stored</th>
                      <th className="py-1 pr-2">inferred</th>
                      <th className="py-1 pr-2">subject</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currencyCensus.signal_contradicts_stored_samples.map((s) => (
                      <tr key={s.id} className="border-b border-border/30">
                        <td className="py-1 pr-2 font-mono">{s.id}</td>
                        <td className="py-1 pr-2 font-mono">{s.stored_currency}</td>
                        <td className="py-1 pr-2 font-mono">{s.inferred_currency}</td>
                        <td className="py-1 pr-2">{s.email_subject}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {sourceVerification && (
          <div className="border-t pt-3 space-y-2 text-xs" data-testid="source-verification-result">
            <div className="font-medium">Approved-zero source verification (live Pennylane/Xero)</div>
            <p className="text-muted-foreground">
              Per doc: does the source system confirm a genuine zero, report a nonzero amount
              (a sync artifact -- real money missing from aggregates), 404 as an orphan, or
              fail to check (auth/network/rate-limit)? Read-only -- no invoice doc is ever
              rewritten here.
            </p>

            <div className="grid grid-cols-3 sm:grid-cols-6 gap-x-4 gap-y-1" data-testid="source-verification-tallies">
              {Object.entries(sourceVerification.tallies || {}).map(([k, v]) => (
                <div key={k}>
                  <span className="text-muted-foreground">{VERDICT_LABELS[k] || k}:</span>{' '}
                  <span className="font-medium">{v}</span>
                </div>
              ))}
            </div>

            {sourceVerification.total_count != null && (
              <div className="text-muted-foreground">
                Total: {sourceVerification.total_count}
                {sourceVerification.tallies_sum_matches_population === false && (
                  <span className="text-destructive"> (tallies do not sum to population -- investigate)</span>
                )}
              </div>
            )}

            {sourceVerification.source_has_amount_delta_by_currency &&
              Object.keys(sourceVerification.source_has_amount_delta_by_currency).length > 0 && (
                <div>
                  <span className="text-muted-foreground">Money invisible to aggregates (source_has_amount, by currency):</span>{' '}
                  {Object.entries(sourceVerification.source_has_amount_delta_by_currency).map(([ccy, total]) => (
                    <span key={ccy} className="font-medium mr-3">{total} {ccy}</span>
                  ))}
                </div>
              )}

            {sourceVerification.rows?.length > 0 && (
              <table className="w-full text-xs border-collapse mt-1">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-1 pr-2">Doc id</th>
                    <th className="py-1 pr-2">Source</th>
                    <th className="py-1 pr-2">Source doc id</th>
                    <th className="py-1 pr-2 text-right">Stored</th>
                    <th className="py-1 pr-2 text-right">Source amount</th>
                    <th className="py-1 pr-2">Verdict</th>
                    <th className="py-1 pr-2">Pair</th>
                    <th className="py-1 pr-2">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {sourceVerification.rows.map((r) => (
                    <tr key={r.invoice_id} className="border-b border-border/30">
                      <td className="py-1 pr-2 font-mono">{r.invoice_id}</td>
                      <td className="py-1 pr-2 font-mono">{r.source}</td>
                      <td className="py-1 pr-2 font-mono">{r.external_id || '--'}</td>
                      <td className="py-1 pr-2 text-right font-mono">{r.stored_amount ?? 0}</td>
                      <td className="py-1 pr-2 text-right font-mono">
                        {r.source_amount != null ? `${r.source_amount} ${r.source_currency || ''}` : '--'}
                      </td>
                      <td className="py-1 pr-2 font-mono">{VERDICT_LABELS[r.verdict] || r.verdict}</td>
                      <td className="py-1 pr-2 font-mono">
                        {r.sibling
                          ? `${r.sibling.sibling_id} (${r.sibling.sibling_amount} ${r.sibling.sibling_review_status || ''})`
                          : '--'}
                      </td>
                      <td className="py-1 pr-2">{r.error_text || '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="border-t pt-3 space-y-2 text-xs">
          <div className="font-medium">Zero-amount closeout: duplicate merge (run first)</div>
          <p className="text-muted-foreground">
            A zero-amount doc whose invoice_number matches a sibling doc carrying a real amount
            has nothing of its own to pay -- it merges into the sibling instead of becoming a
            payable. A sibling that is not itself approved lists as needs-human-look and is
            never auto-merged. Must run (and Apply) before the terminal step below -- a doc with
            a pending sibling here is skipped by terminal Apply regardless of order.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button data-testid="zero-amount-duplicate-merge-preview" onClick={runDuplicateMergePreview} disabled={dupMergeLoading} size="sm" variant="outline">
              {dupMergeLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Preview duplicate merge
            </Button>
            <Button
              data-testid="zero-amount-duplicate-merge-apply"
              onClick={applyDuplicateMerge}
              disabled={dupMergeApplyLoading || !dupMergePreview?.mergeable_count}
              size="sm"
            >
              {dupMergeApplyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Apply duplicate merge
            </Button>
          </div>

          {dupMergePreview && (
            <div className="border rounded-lg p-3 space-y-2" data-testid="zero-amount-duplicate-merge-result">
              <div className="flex flex-wrap gap-4">
                <span>Mergeable: <span className="font-medium">{dupMergePreview.mergeable_count}</span></span>
                <span>Needs human look: <span className="font-medium">{dupMergePreview.needs_human_look_count}</span></span>
              </div>
              {dupMergePreview.mergeable?.length > 0 && (
                <table className="w-full text-xs border-collapse mt-1">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1 pr-2">Zero doc</th>
                      <th className="py-1 pr-2">Surviving sibling</th>
                      <th className="py-1 pr-2 text-right">Sibling amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dupMergePreview.mergeable.map((e) => (
                      <tr key={e.zero_doc_id} className="border-b border-border/30">
                        <td className="py-1 pr-2 font-mono">{e.zero_doc_id}</td>
                        <td className="py-1 pr-2 font-mono">{e.sibling_id}</td>
                        <td className="py-1 pr-2 text-right font-mono">{e.sibling_amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {dupMergePreview.needs_human_look?.length > 0 && (
                <div>
                  <div className="font-medium mt-1">Needs human look (sibling not approved)</div>
                  <table className="w-full text-xs border-collapse mt-1">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1 pr-2">Zero doc</th>
                        <th className="py-1 pr-2">Sibling</th>
                        <th className="py-1 pr-2">Sibling review_status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dupMergePreview.needs_human_look.map((e) => (
                        <tr key={e.zero_doc_id} className="border-b border-border/30">
                          <td className="py-1 pr-2 font-mono">{e.zero_doc_id}</td>
                          <td className="py-1 pr-2 font-mono">{e.sibling_id}</td>
                          <td className="py-1 pr-2 font-mono">{e.sibling_review_status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t pt-3 space-y-2 text-xs">
          <div className="font-medium">Zero-amount closeout: terminal "nothing due" (run second)</div>
          <p className="text-muted-foreground">
            Docs with a persisted genuinely-zero-in-source verdict and no pending sibling get
            review_status="zero_amount_nothing_due" (excluded from aggregates, matching, and
            accounting sync, but still visible on the Invoices page). A later re-verification
            that contradicts the verdict automatically reverts the doc back to "approved".
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button data-testid="zero-amount-terminal-preview" onClick={runTerminalPreview} disabled={terminalLoading} size="sm" variant="outline">
              {terminalLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Preview nothing-due
            </Button>
            <Button
              data-testid="zero-amount-terminal-apply"
              onClick={applyTerminal}
              disabled={terminalApplyLoading || !terminalPreview?.eligible_count}
              size="sm"
            >
              {terminalApplyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Apply nothing-due
            </Button>
          </div>

          {terminalPreview && (
            <div className="border rounded-lg p-3 space-y-2" data-testid="zero-amount-terminal-result">
              <div className="flex flex-wrap gap-4">
                <span>Eligible: <span className="font-medium">{terminalPreview.eligible_count}</span></span>
                <span>Skipped (pending sibling): <span className="font-medium">{terminalPreview.skipped_pending_sibling_count}</span></span>
                <span>Skipped (no verified-zero verdict): <span className="font-medium">{terminalPreview.skipped_no_verified_zero_verdict_count}</span></span>
              </div>
              {terminalPreview.eligible?.length > 0 && (
                <table className="w-full text-xs border-collapse mt-1">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1 pr-2">Doc id</th>
                      <th className="py-1 pr-2">Source</th>
                      <th className="py-1 pr-2">Verdict fetched at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {terminalPreview.eligible.map((e) => (
                      <tr key={e.zero_doc_id} className="border-b border-border/30">
                        <td className="py-1 pr-2 font-mono">{e.zero_doc_id}</td>
                        <td className="py-1 pr-2 font-mono">{e.source}</td>
                        <td className="py-1 pr-2 font-mono">{fmtTimestamp(e.verdict_fetched_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        <div className="border-t pt-3 space-y-2 text-xs">
          <div className="font-medium">Disposition: census cohort explain table</div>
          <p className="text-muted-foreground">
            One row per doc in the census's own cohort -- which duplicate-merge and terminal
            bucket it landed in and why. The permanent answer to "census says X, preview says
            Y" for this card; merge/terminal previews above and this table share the exact same
            population query, so they can never disagree on who is in scope.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button data-testid="zero-amount-disposition-run" onClick={runDisposition} disabled={dispositionLoading} size="sm" variant="outline">
              {dispositionLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Run disposition
            </Button>
          </div>

          {disposition && (
            <div className="border rounded-lg p-3 space-y-2" data-testid="zero-amount-disposition-result">
              <div>Cohort total: <span className="font-medium">{disposition.cohort_total}</span></div>
              {disposition.rows?.length > 0 && (
                <table className="w-full text-xs border-collapse mt-1">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1 pr-2">Doc id</th>
                      <th className="py-1 pr-2">Source</th>
                      <th className="py-1 pr-2">review_status</th>
                      <th className="py-1 pr-2">Verdict</th>
                      <th className="py-1 pr-2">Sibling</th>
                      <th className="py-1 pr-2">Merge bucket</th>
                      <th className="py-1 pr-2">Terminal bucket</th>
                    </tr>
                  </thead>
                  <tbody>
                    {disposition.rows.map((r) => (
                      <tr key={r.doc_id} className="border-b border-border/30">
                        <td className="py-1 pr-2 font-mono">{r.doc_id}</td>
                        <td className="py-1 pr-2 font-mono">{r.source}</td>
                        <td className="py-1 pr-2 font-mono">{r.review_status}</td>
                        <td className="py-1 pr-2 font-mono">{r.verdict_found ? r.verdict : 'none'}</td>
                        <td className="py-1 pr-2 font-mono">
                          {r.sibling_id ? `${r.sibling_id} (${r.sibling_review_status || ''})` : '--'}
                        </td>
                        <td className="py-1 pr-2 font-mono">{r.merge_bucket}</td>
                        <td className="py-1 pr-2 font-mono">{r.terminal_bucket}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {parsedPairCensus && (
          <div className="border rounded-lg p-3 space-y-2 text-xs" data-testid="parsed-pair-census-result">
            <div className="font-medium">Parsed-pair duplicate census (both sides already parsed successfully)</div>
            <p className="text-muted-foreground">{parsedPairCensus.note}</p>
            <div className="flex flex-wrap gap-4">
              <span>Scanned: <span className="font-medium">{parsedPairCensus.total_scanned}</span></span>
              <span>Pairs found: <span className="font-medium">{parsedPairCensus.pair_count}</span></span>
              <span>Already flagged: <span className="font-medium">{parsedPairCensus.already_flagged_count}</span></span>
            </div>
            {parsedPairCensus.samples?.length > 0 && (
              <table className="w-full text-xs border-collapse mt-1">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-1 pr-2">Receipt doc</th>
                    <th className="py-1 pr-2">Invoice doc</th>
                    <th className="py-1 pr-2">Vendor</th>
                    <th className="py-1 pr-2 text-right">Amount</th>
                    <th className="py-1 pr-2">Flagged</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedPairCensus.samples.map((s) => (
                    <tr key={`${s.receipt_doc_id}-${s.invoice_doc_id}`} className="border-b border-border/30">
                      <td className="py-1 pr-2 font-mono">{s.receipt_doc_id}</td>
                      <td className="py-1 pr-2 font-mono">{s.invoice_doc_id}</td>
                      <td className="py-1 pr-2">{s.vendor}</td>
                      <td className="py-1 pr-2 text-right font-mono">{s.amount} {s.currency}</td>
                      <td className="py-1 pr-2">{s.already_flagged ? 'yes' : 'no'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
